/**
 * FirmaBilgileriDuzenleModal.jsx — Firma Kartı › Firma Bilgileri'ni DÜZENLE (backlog #438, 2026-10-16).
 *
 * Firmanın kendi bilgileri tek modalda: ad, kısa ad, adres, e-posta, telefon, web sitesi ve vergi numarası.
 * Yazma yetkisi RBAC'a bağlıdır (#436, karar: FIRMA_PROFILI modülü): sayfa bu modalı yalnızca portal bağlamında
 * FIRMA_PROFILI en az WRITE olan girişe açar; backend aynı kuralı 403 ile zorlar.
 *
 * Değiştirilemeyenler SALT OKUNUR ve nedeniyle gösterilir: uyruk (Yerli / Yabancı) ve ülke yalnızca SAKA personelinin
 * (#312). Firma girişi için modal bu ikisini HİÇ göndermez; backend de bir firma girişinden gelen ülke / uyruk değişikliğini
 * reddeder (ülke Türkiye ise uyruk Yerli'ye döndüğü için ülke de uyruk kadar korunur).
 * #554 `staff` kipi (Firma Detay › Profil & Belgeler'in Düzenle'si bu modalı açar): uyruk ve ülke DÜZENLENİR. Ülke adıyla
 * seçilir (UlkeSecici), kural firmaUyruk.js'te; yalnızca DEĞİŞTİYSE gider (uyruk + ülke birlikte), ülkesi 0 olan bir Yerli
 * firmaya dokunulmaz. Uyruğun belge gruplarını ve portal setini değiştirdiği formda söylenir.
 * Firma adı, Bilge'nin 2026-10-16 kararıyla firmanın kendisi tarafından değiştirilebilir; onaylı belgelerde ve SAKA
 * kayıtlarında bu adla görüneceği için formda uyarı basılır.
 *
 * İki istek: kimlik (PUT /firma/{id}: ad, kısa ad, vergi no) ve iletişim (PUT /firma/{id}/profile: adres, e-posta,
 * telefon, web sitesi). İkisi de PATCH: yalnızca DEĞİŞEN alan gönderilir. İkisi tek işlemde değil; biri yazılıp diğeri
 * başarısız olursa hangisinin kaydedildiği söylenir.
 *
 * Vergi numarası: Yerli firmada 10 haneli VKN / 11 haneli TCKN (vergiNo.js, backend ile aynı kural); Yabancı firmada
 * isteğe bağlı serbest metin. Vergi numarasını yalnızca değeri okunabilmişse (canlı fuar) gösterir.
 */
import React, { useState } from "react";
import { Alert, Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { firmaService } from "../../services/firmaService";
import { saveFirmaProfile } from "../../services/firmaMediaService";
import { vergiNoHatasi, vergiNoTemizle, VERGI_NO_MAX } from "./vergiNo";
import UlkeSecici from "./UlkeSecici";
import TelefonGirdisi from "../Common/TelefonGirdisi";
import { baslangicUyruk, uyrukDegisinceUlke, uyrukGovdesi } from "./firmaUyruk";
import { FIRMA_NATION } from "../../services/firmaAramaService";

const AD_MAX = 255;
const KISA_AD_MAX = 100;

/** Boş / boşluk = yok. */
const temiz = (v) => String(v ?? "").trim();

/**
 * @param {object}  profile        profile-page.profile: firmaName, firmaShortName, adres, eposta, telefon, webSitesi
 * @param {string}  taxNumber      kayıtlı vergi numarası (null = yok)
 * @param {boolean} vergiNoGoster  vergi numarasının değeri okunabildi mi (canlı fuar)
 * @param {boolean} yabanci        Yabancı firma: vergi numarası isteğe bağlı
 * @param {boolean} staff          #554: SAKA personeli — uyruk ve ülke düzenlenir (profile.firmaNation / countryId'den dolar)
 * @param {Function} onSaved       kaydedildikten (ya da kısmen) sonra çağrılır; sayfa tazelenir
 */
export default function FirmaBilgileriDuzenleModal({ toggle, firmaId, profile, taxNumber, vergiNoGoster, yabanci, staff = false, onSaved }) {
  const { t } = useTranslation("components");
  // Modal yalnızca açıkken mount edilir: form, prop'tan useState başlangıcıyla dolar (effect ile senkronizasyon yok).
  const [form, setForm] = useState(() => ({
    firmaName: profile?.firmaName || "",
    firmaShortname: profile?.firmaShortName || "",
    adres: profile?.adres || "",
    eposta: profile?.eposta || "",
    telefon: profile?.telefon || "",
    webSitesi: profile?.webSitesi || "",
    taxNumber: taxNumber || "",
  }));
  const [saving, setSaving] = useState(false);
  const [hata, setHata] = useState(null);
  // #554 (yalnızca personel): açılıştaki uyruk + ülke ve formdaki hâli. Firma girişinde `yabanci` prop'u geçerli kalır.
  const [baslangic] = useState(() => baslangicUyruk(profile, yabanci));
  const [uyruk, setUyruk] = useState(baslangic);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const yabanciGecerli = staff ? uyruk.firmaNation === FIRMA_NATION.YABANCI : yabanci;
  const uyrukDegisti = staff && Object.keys(uyrukGovdesi(baslangic, uyruk)).length > 0;
  const uyrukSec = (e) => {
    const firmaNation = Number(e.target.value);
    setUyruk((u) => ({ firmaNation, countryId: uyrukDegisinceUlke(u.countryId, firmaNation) }));
  };

  // WAS (until #554): vergiNoHatasi(form.taxNumber, !yabanci)
  const vergiHata = vergiNoGoster ? vergiNoHatasi(form.taxNumber, !yabanciGecerli) : null;
  const adGecerli = temiz(form.firmaName) !== "";

  async function kaydet() {
    if (!adGecerli || vergiHata) return;
    setSaving(true);
    setHata(null);

    // yalnızca DEĞİŞEN alanlar (PATCH): kimlik ve iletişim ayrı istekler
    const kimlik = {};
    const adDegisti = temiz(form.firmaName) !== temiz(profile?.firmaName);
    const kisaAdDegisti = temiz(form.firmaShortname) !== temiz(profile?.firmaShortName);
    const vergiDegisti = vergiNoGoster && vergiNoTemizle(form.taxNumber) !== vergiNoTemizle(taxNumber);
    const uyrukAlanlari = staff ? uyrukGovdesi(baslangic, uyruk) : {};
    if (adDegisti || kisaAdDegisti || vergiDegisti || Object.keys(uyrukAlanlari).length > 0) {
      // firmaName backend'de her zaman zorunlu; uyruk / ülke ANAHTARLARI yalnızca personel kipinde ve yalnızca değiştiyse gider
      // (firma girişi için bilerek hiç gönderilmez: backend reddeder).
      kimlik.firmaName = temiz(form.firmaName);
      if (kisaAdDegisti) kimlik.firmaShortname = temiz(form.firmaShortname);
      if (vergiDegisti) kimlik.taxNumber = vergiNoTemizle(form.taxNumber) || null;
      Object.assign(kimlik, uyrukAlanlari);
    }
    const iletisim = {};
    [["adres", "adres"], ["eposta", "eposta"], ["telefon", "telefon"], ["webSitesi", "webSitesi"]].forEach(([anahtar, alan]) => {
      if (temiz(form[anahtar]) !== temiz(profile?.[alan])) iletisim[anahtar] = temiz(form[anahtar]) || null;
    });

    if (Object.keys(kimlik).length === 0 && Object.keys(iletisim).length === 0) {
      setSaving(false);
      toggle();
      return;
    }

    let kimlikYazildi = false;
    try {
      if (Object.keys(kimlik).length > 0) {
        await firmaService.updateFirma(firmaId, kimlik);
        kimlikYazildi = true;
      }
      if (Object.keys(iletisim).length > 0) {
        await saveFirmaProfile(firmaId, iletisim);
      }
      onSaved?.({ tamam: true });
    } catch (e) {
      const mesaj = e.response?.data?.message || e.message;
      if (kimlikYazildi) {
        // kimlik yazıldı, iletişim yazılamadı: sayfa yine tazelenir, modal açık kalır ve ne olduğunu söyler
        setHata(t("firmaProfilModallari.kimlikKaydedildiIletisimKaydedilemedi", { mesaj }));
        onSaved?.({ tamam: false });
      } else {
        setHata(mesaj);
      }
      setSaving(false);
    }
  }

  return (
    <Modal isOpen toggle={saving ? undefined : toggle} size="lg">
      <ModalHeader toggle={saving ? undefined : toggle}>{t("firmaProfilModallari.firmaBilgileriniDuzenle")}</ModalHeader>
      <ModalBody>
        {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}

        <Row className="g-2">
          <Col md={8}>
            <Label className="small" for="fb-ad">{t("firmaProfilModallari.firmaAdi")}</Label>
            <Input id="fb-ad" bsSize="sm" value={form.firmaName} maxLength={AD_MAX} onChange={set("firmaName")} invalid={!adGecerli} />
          </Col>
          <Col md={4}>
            <Label className="small" for="fb-kisa">{t("firmaProfilModallari.firmaKisaAdi")}</Label>
            <Input id="fb-kisa" bsSize="sm" value={form.firmaShortname} maxLength={KISA_AD_MAX} onChange={set("firmaShortname")} />
          </Col>
        </Row>
        <div className="text-muted small mt-1 mb-3">{t("firmaProfilModallari.adUyarisi")}</div>

        <Row className="g-2">
          <Col md={12}>
            <Label className="small" for="fb-adres">{t("firmaProfilModallari.adres")}</Label>
            <Input id="fb-adres" type="textarea" rows={2} bsSize="sm" value={form.adres} onChange={set("adres")} />
          </Col>
          <Col md={4}>
            <Label className="small" for="fb-eposta">{t("firmaProfilModallari.ePosta")}</Label>
            <Input id="fb-eposta" type="email" bsSize="sm" value={form.eposta} onChange={set("eposta")} />
          </Col>
          <Col md={4}>
            <Label className="small" for="fb-tel">{t("firmaProfilModallari.telefon")}</Label>
            <TelefonGirdisi id="fb-tel" bsSize="sm" value={form.telefon} onChange={(v) => setForm((f) => ({ ...f, telefon: v }))} />
          </Col>
          <Col md={4}>
            <Label className="small" for="fb-web">{t("firmaProfilModallari.webSitesi")}</Label>
            <Input id="fb-web" bsSize="sm" value={form.webSitesi} onChange={set("webSitesi")} />
          </Col>
        </Row>

        {/* #554 (personel): uyruk + ülke, vergi numarasının üstünde çünkü vergi kuralını uyruk belirler. */}
        {staff && (
          <div className="mt-3">
            <Row className="g-2">
              <Col md={6}>
                <Label className="small" for="fb-uyruk">{t("firmaProfilModallari.uyruk")}</Label>
                <Input id="fb-uyruk" type="select" bsSize="sm" value={uyruk.firmaNation} onChange={uyrukSec}>
                  <option value={FIRMA_NATION.YERLI}>{t("firmaProfilModallari.yerli")}</option>
                  <option value={FIRMA_NATION.YABANCI}>{t("firmaProfilModallari.yabanci")}</option>
                </Input>
              </Col>
              <Col md={6}>
                <Label className="small" for="fb-ulke">{t("firmaProfilModallari.ulke")}</Label>
                <UlkeSecici
                  inputId="fb-ulke" value={uyruk.countryId} turkiyeHaric disabled={!yabanciGecerli}
                  onChange={(countryId) => setUyruk((u) => ({ ...u, countryId }))}
                />
              </Col>
            </Row>
            <div className="text-muted small mt-1">
              {yabanciGecerli ? t("firmaProfilModallari.yabanciUlkeIpucu") : t("firmaProfilModallari.yerliUlkeIpucu")}
            </div>
            <div className={`small mt-1 ${uyrukDegisti ? "text-warning fw-semibold" : "text-muted"}`}>
              {t("firmaProfilModallari.uyrukDegisimUyarisi")}
            </div>
          </div>
        )}

        {vergiNoGoster && (
          <div className="mt-3">
            <Label className="small" for="fb-vergi">
              {yabanciGecerli ? t("firmaProfilModallari.vergiKimlikNoIsteginBagli") : t("firmaProfilModallari.vergiNoTckn")}
            </Label>
            <Input
              id="fb-vergi" bsSize="sm" value={form.taxNumber} maxLength={VERGI_NO_MAX} style={{ maxWidth: 260 }}
              onChange={set("taxNumber")} invalid={Boolean(vergiHata)}
            />
            {vergiHata
              ? <div className="text-danger small mt-1">{vergiHata}</div>
              : <div className="text-muted small mt-1">{yabanciGecerli ? t("firmaProfilModallari.vergiYabanciIpucu") : t("firmaProfilModallari.vergiYerliIpucu")}</div>}
          </div>
        )}

        {/* Değiştirilemeyenler (firma girişi): neden yazılı. Bu iki alan istekte HİÇ gönderilmez. #554: personelde yukarıda düzenlenir. */}
        {!staff && (
          <div className="border rounded bg-light p-2 mt-3">
            <div className="d-flex align-items-center gap-1 small fw-semibold mb-1">
              <Lock size={13} />{t("firmaProfilModallari.degistirilemeyenler")}
            </div>
            <Row className="small g-2">
              <Col md={6}>
                <span className="text-muted">{t("firmaProfilModallari.uyruk")}</span>{" "}
                <span className="fw-semibold">{yabanci ? t("firmaProfilModallari.yabanci") : t("firmaProfilModallari.yerli")}</span>
              </Col>
            </Row>
            <div className="text-muted small mt-1">{t("firmaProfilModallari.uyrukUlkeNedeni")}</div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle} disabled={saving}>{t("common:iptal")}</Button>
        <Button color="primary" onClick={kaydet} disabled={saving || !adGecerli || Boolean(vergiHata)}>
          {saving ? <Spinner size="sm" /> : t("common:kaydet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
